import type { AgendaModule, Document } from "@repo/types";
import { FormDialog, type FormDialogFormProps } from "../dialog/FormDialog";
import { useMemo, useState } from "react";
import { useTenantStore } from "@/stores/tenantStore";
import { AgendaItemForm } from "../agendaItems/AgendaItemForm";
import { PlusCircleIcon } from "lucide-react";
import { SearchableTable, type SearchableTableProps } from "./SearchableTable";
import {
  createCreatedAtColumn,
  createCreatedByColumn,
  createDescriptionColumn,
  createExpirationDateColumn,
  createIdColumn,
  createNameColumn,
  createStatusColumn,
  createTagColumn,
  createTeamColumn,
  createUpdatedAtColumn,
  createUpdatedByColumn,
} from "../dataTable/columns";
import {
  AgendaItemDocApprovalTypeColumn,
  AgendaItemGASB87,
  AgendaItemGASB96,
  createAgendaItemPendingColumn,
  AgendaItemRequestedApprovalDateColumn,
  AgendaItemReviewStatusColumn,
  createAgendaItemApproverColumn,
  createAgendaItemDocumentsColumn,
  createAgendaItemUsedByColumn,
  GLAccountColumn,
} from "../agendaItems/AgendaItemsDataTableColumns";
import { Button } from "@repo/ui/components/Base/Button";
import { AgendaModuleForm } from "../agendaModules/AgendaModuleForm";
import { DocumentForm } from "../documents/DocumentForm";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import {
  createDocumentUsedByColumn,
  DocumentAttachmentsColumn,
  DocumentConfidentialColumn,
  DocumentNameColumn,
  DocumentUploadTypeColumn,
  OrdinanceCategoryColumn,
  OrdinanceNumberColumn,
  OrdinanceStatusColumn,
} from "../documents/DocumentsDataTableColumns";
import {
  AGENDA_ITEM_MOTIF,
  AGENDA_MODULE_MOTIF,
  DOCUMENT_MOTIF,
  KEYS,
} from "@/lib/constants";
import { cn } from "@repo/shared/utils";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { useMemoObjectList } from "@/hooks/useMemoObjectList";
import { type TableLayoutState } from "@repo/ui/components/Base/DataTable";
import {
  createAgendaModuleAgendaItemsColumn,
  createAgendaModuleUsedByColumn,
} from "../agendaModules/AgendaModulesDataTableColumns";
import type { AgendaItemDto } from "@/repositories/agendaItemRepo";

interface BaseSearchableTableProps<T>
  extends Pick<SearchableTableProps<T>, "data"> {
  onAdd: (item: T) => void;
}

const searchableDocumentsTableDefaultLayout: TableLayoutState = {
  columnVisibility: {
    id: false,
    status: false,
    createdAt: false,
    createdBy: false,
    updatedAt: false,
    updatedBy: false,
    teamId: false,
    usedBy: false,
    uploadType: false,
    ordinanceNumber: false,
    ordinanceCategory: false,
    ordinanceStatus: false,
  },
};

export const SearchableDocumentsTable = ({
  showCreate = true,
  onSubmit,
  onAdd,
  newPartialDocument,
  disabled = false,
  data,
  ...props
}: BaseSearchableTableProps<Document> & {
  showCreate?: boolean;
  onSubmit?: (document: Document) => void;
  newPartialDocument?: Partial<Document>;
  disabled?: boolean;
}) => {
  const tenantId = useTenantStore((s) => s.id);
  const [createOpen, setCreateOpen] = useState(false);

  onAdd = useFutureCallback(onAdd);

  const columns: ColumnDef<Document>[] = useMemo(
    () => [
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex flex-row align-middle gap-1 justify-between">
            <Button
              disabled={disabled}
              variant="ghost"
              size="sm"
              tooltip="Add this document"
              tooltipProps={{
                tooltipTriggerProps: { className: "w-full text-left" },
              }}
              onClick={() => onAdd(row.original)}
            >
              <PlusCircleIcon className="pr-2" /> Add
            </Button>
          </div>
        ),
        enableHiding: false,
      },
      createIdColumn<Document>(),
      DocumentNameColumn,
      createTagColumn<Document>(),
      createStatusColumn(),
      createDescriptionColumn<Document>(),
      DocumentConfidentialColumn,
      createCreatedAtColumn(),
      createCreatedByColumn(tenantId),
      createUpdatedAtColumn(),
      createUpdatedByColumn(tenantId),
      createTeamColumn(tenantId),
      createDocumentUsedByColumn(tenantId),
      DocumentAttachmentsColumn,
      DocumentUploadTypeColumn,
      OrdinanceNumberColumn,
      OrdinanceCategoryColumn,
      OrdinanceStatusColumn,
    ],
    [tenantId, onAdd, disabled]
  );

  const handleCreate = useFutureCallback(() => setCreateOpen(true));
  const handleRowClick = useFutureCallback((row: Row<Document>) => {
    if (!disabled) onAdd(row.original);
  });

  // prevent re-rendering SearchableTable if list's ids didn't change
  data = useMemoObjectList(data);

  return (
    <>
      <FormDialog
        open={createOpen}
        setOpen={setCreateOpen}
        title="Create Document"
        preventDefault
        showTrigger={false}
        headerClassName={DOCUMENT_MOTIF}
        usesWithinDialog
        form={({
          handleCancel = () => {},
          handleComplete = () => {},
        }: FormDialogFormProps) => (
          <DocumentForm
            tenantId={tenantId}
            partialDocumentFormData={newPartialDocument}
            handleCancel={handleCancel}
            handleComplete={handleComplete}
            onSubmit={onSubmit}
            withinDialog
          />
        )}
      />
      <SearchableTable
        className={cn(disabled && "opacity-60")}
        label="Select Attachments (Optional)"
        createLabel="Create Document"
        onCreate={handleCreate}
        showCreate={showCreate}
        onRowClick={handleRowClick}
        columns={columns}
        data={data}
        savedLayoutKey={KEYS.SEARCHABLE_DOCUMENT_TABLE_COLUMNS}
        layoutDefaults={searchableDocumentsTableDefaultLayout}
        {...props}
      />
    </>
  );
};

const searchableAgendaItemTableDefaultLayout: TableLayoutState = {
  columnVisibility: {
    id: false,
    docapprovaltype: false,
    createdAt: false,
    createdBy: false,
    updatedAt: false,
    updatedBy: false,
    expirationDate: false,
    usedBy: false,
    documents: false,
    teamId: false,
    status: false,
    glAccount: false,
    lease: false,
    software: false,
  },
  columnFilters: [
    { id: "pending", value: true },
    { id: "approver", value: { type: "team" } },
  ],
  sorting: [{ id: "requestedApprovalDate", desc: false }],
};

export const SearchableAgendaItemTable = ({
  showCreate = true,
  onSubmit,
  onAdd,
  disabled = false,
  newPartialAgendaItem,
  data,
  ...props
}: BaseSearchableTableProps<AgendaItemDto> & {
  showCreate?: boolean;
  onSubmit?: (agendaItem: AgendaItemDto) => void;
  disabled?: boolean;
  newPartialAgendaItem?: Partial<AgendaItemDto>;
}) => {
  const tenantId = useTenantStore((s) => s.id);
  const [createOpen, setCreateOpen] = useState(false);

  onAdd = useFutureCallback(onAdd);

  const columns: ColumnDef<AgendaItemDto>[] = useMemo(
    () => [
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex flex-row align-middle gap-1 justify-between ">
            <Button
              disabled={disabled}
              variant="ghost"
              size="sm"
              tooltip="Add this agenda item"
              tooltipProps={{
                tooltipTriggerProps: { className: "w-full text-left" },
              }}
              onClick={() => onAdd(row.original)}
            >
              <PlusCircleIcon className="pr-2" /> Add
            </Button>
          </div>
        ),
        enableHiding: false,
      },
      createIdColumn<AgendaItemDto>(),
      createNameColumn<AgendaItemDto>(),
      createAgendaItemPendingColumn(tenantId),
      createTagColumn<AgendaItemDto>(),
      createDescriptionColumn<AgendaItemDto>(),
      createStatusColumn(),
      createCreatedAtColumn(),
      createCreatedByColumn(tenantId),
      createUpdatedAtColumn(),
      createUpdatedByColumn(tenantId),
      createTeamColumn(tenantId),
      AgendaItemRequestedApprovalDateColumn,
      AgendaItemDocApprovalTypeColumn,
      createAgendaItemApproverColumn(tenantId),
      AgendaItemReviewStatusColumn,
      createExpirationDateColumn(),
      GLAccountColumn,
      AgendaItemGASB87,
      AgendaItemGASB96,
      createAgendaItemUsedByColumn(tenantId),
      createAgendaItemDocumentsColumn(tenantId),
    ],
    [onAdd, disabled, tenantId]
  );

  const handleCreate = useFutureCallback(() => setCreateOpen(true));
  const handleRowClick = useFutureCallback((row: Row<AgendaItemDto>) => {
    if (!disabled) onAdd(row.original);
  });

  // prevent re-rendering SearchableTable if list's ids didn't change
  data = useMemoObjectList(data);

  return (
    <>
      <FormDialog
        open={createOpen}
        setOpen={setCreateOpen}
        title="Create Agenda Item"
        preventDefault
        showTrigger={false}
        headerClassName={AGENDA_ITEM_MOTIF}
        usesWithinDialog
        form={({
          handleCancel = () => {},
          handleComplete = () => {},
        }: FormDialogFormProps) => (
          <AgendaItemForm
            tenantId={tenantId}
            partialAgendaItemFormData={newPartialAgendaItem}
            handleCancel={handleCancel}
            handleComplete={handleComplete}
            onSubmit={onSubmit}
            withinDialog
          />
        )}
      />
      <SearchableTable
        className={cn(disabled && "opacity-60")}
        label="Select Agenda Items"
        createLabel="Create Agenda Item"
        onCreate={handleCreate}
        showCreate={showCreate}
        onRowClick={handleRowClick}
        columns={columns}
        data={data}
        savedLayoutKey={KEYS.SEARCHABLE_AGENDAITEM_TABLE_COLUMNS}
        layoutDefaults={searchableAgendaItemTableDefaultLayout}
        {...props}
      />
    </>
  );
};

const searchableAgendaModuleTableDefaultLayout: TableLayoutState = {
  columnVisibility: {
    id: false,
    description: false,
    createdAt: false,
    updatedAt: false,
    createdBy: false,
    updatedBy: false,
    teamId: false,
    usedBy: false,
    agendaItems: false,
  },
};

export const SearchableAgendaModuleTable = ({
  isTemplate = false,
  showCreate = true,
  onSubmit,
  onAdd,
  disabled = false,
  data,
  ...props
}: BaseSearchableTableProps<AgendaModule> & {
  isTemplate?: boolean;
  showCreate?: boolean;
  onSubmit?: (agendaModule: AgendaModule) => void;
  disabled?: boolean;
}) => {
  const tenantId = useTenantStore((s) => s.id);
  const [createOpen, setCreateOpen] = useState(false);

  onAdd = useFutureCallback(onAdd);

  const columns: ColumnDef<AgendaModule>[] = useMemo(
    () => [
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          return (
            <div className="flex flex-row align-middle gap-1 justify-between">
              <Button
                disabled={disabled}
                variant="ghost"
                size="sm"
                tooltip="Add this agenda section"
                tooltipProps={{
                  tooltipTriggerProps: { className: "w-full text-left" },
                }}
                onClick={() => onAdd(row.original)}
              >
                <PlusCircleIcon className="pr-2" /> Add
              </Button>
            </div>
          );
        },
        enableHiding: false,
      },
      createIdColumn<AgendaModule>(),
      createNameColumn<AgendaModule>(),
      createTagColumn<AgendaModule>(),
      createDescriptionColumn(),
      createStatusColumn(),
      createCreatedAtColumn(),
      createCreatedByColumn(tenantId),
      createUpdatedAtColumn(),
      createUpdatedByColumn(tenantId),
      createTeamColumn(tenantId),
      createAgendaModuleUsedByColumn(tenantId),
      createAgendaModuleAgendaItemsColumn(tenantId),
    ],
    [tenantId, onAdd, disabled]
  );

  const handleCreate = useFutureCallback(() => setCreateOpen(true));
  const handleRowClick = useFutureCallback((row: Row<AgendaModule>) => {
    if (!disabled) onAdd(row.original);
  });

  // prevent re-rendering SearchableTable if list's ids didn't change
  data = useMemoObjectList(data);

  return (
    <>
      <FormDialog
        open={createOpen}
        setOpen={setCreateOpen}
        title="Create Agenda Section"
        preventDefault
        showTrigger={false}
        headerClassName={AGENDA_MODULE_MOTIF}
        usesWithinDialog
        form={({
          handleCancel = () => {},
          handleComplete = () => {},
        }: FormDialogFormProps) => (
          <AgendaModuleForm
            isTemplate={isTemplate}
            tenantId={tenantId}
            handleCancel={handleCancel}
            handleComplete={handleComplete}
            onSubmit={onSubmit}
            withinDialog
          />
        )}
      />
      <SearchableTable
        className={cn(disabled && "opacity-60")}
        label="Select Agenda Sections"
        createLabel="Create Agenda Section"
        onCreate={handleCreate}
        showCreate={showCreate}
        onRowClick={handleRowClick}
        columns={columns}
        data={data}
        savedLayoutKey={KEYS.SEARCHABLE_AGENDAMODULE_TABLE_COLUMNS}
        layoutDefaults={searchableAgendaModuleTableDefaultLayout}
        {...props}
      />
    </>
  );
};
